/**
 * Route prefetching — warms the JS chunk (and via `link[rel=modulepreload]`
 * its child chunks) for a target route so navigation feels instant.
 *
 * How to use:
 *
 *   <Link to="/demo" {...prefetchOn("/demo")}>See the demo</Link>
 *
 * The map below intentionally covers only the highest-intent marketing routes.
 * Every entry MUST use the same dynamic-import specifier as the matching
 * `lazyRetry(() => import(...))` in `src/App.tsx` so Vite/Rollup deduplicates
 * the chunk instead of building a second copy.
 */

type Loader = () => Promise<unknown>;

// Keyed by pathname or route pattern. `/blog/:slug` matches any /blog/<slug>.
const LOADERS: Record<string, Loader> = {
  "/demo": () => import("@/pages/Demo"),
  "/blog": () => import("@/pages/Blog"),
  "/blog/:slug": () => import("@/pages/BlogArticle"),
  "/who-we-are": () => import("@/pages/WhoWeAre"),
  "/for-clinicians": () => import("@/pages/ForClinicians"),
  "/signup": () => import("@/pages/Signup"),
  "/login": () => import("@/pages/Login"),
  "/waitlist": () => import("@/pages/Waitlist"),
  "/energy-gap": () => import("@/pages/EnergyGap"),
  "/trust": () => import("@/pages/Trust"),
  "/advisors": () => import("@/pages/Advisors"),
  "/investors": () => import("@/pages/Investors"),
};

const done = new Set<string>();
const inflight = new Map<string, Promise<unknown>>();

const resolveKey = (path: string): string | null => {
  if (LOADERS[path]) return path;
  if (/^\/blog\/[^/]+$/.test(path)) return "/blog/:slug";
  return null;
};

/**
 * Kick off the dynamic import for `path`. Safe to call repeatedly — each
 * route is fetched at most once. Failures are swallowed (prefetch is
 * best-effort; the real navigation will surface any real error).
 */
export function prefetchRoute(path: string): void {
  const key = resolveKey(path);
  if (!key || done.has(key)) return;
  const existing = inflight.get(key);
  if (existing) return;
  const p = LOADERS[key]()
    .then(() => {
      done.add(key);
    })
    .catch(() => {
      // Ignore — do not mark done, allow a later retry.
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, p);
}

/**
 * Handler props for a `<Link>` or `<a>`. Prefetches on hover, focus, or the
 * first touch — all cheap signals of imminent navigation. Pointer/focus fire
 * ~100–400ms before the click, which is usually enough to have the chunk
 * ready by the time React Router mounts the new route.
 */
export function prefetchOn(path: string) {
  const trigger = () => prefetchRoute(path);
  return {
    onMouseEnter: trigger,
    onFocus: trigger,
    onTouchStart: trigger,
  };
}

/**
 * Fire-and-forget prefetch for a batch of routes when the browser is idle.
 * Called once from `<App>` after first paint so the top marketing routes are
 * warm before the visitor even hovers.
 */
export function prefetchOnIdle(paths: string[]): void {
  if (typeof window === "undefined") return;
  // Skip on data-saver or 2g/slow-2g connections.
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (conn?.saveData) return;
  if (conn?.effectiveType && /^(slow-2g|2g)$/.test(conn.effectiveType)) return;

  const run = () => paths.forEach(prefetchRoute);
  const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;

  // Give first paint, hydration, and Core Web Vitals time to settle before
  // speculative work. Hover/focus prefetch remains immediate.
  setTimeout(() => {
    if (ric) ric(run, { timeout: 3000 });
    else run();
  }, 5000);
}
